import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import ShoppingCart from "../assets/dummyimage.jpg";
import HandSwipe from "../assets/handSwipe.svg";
import { DEFAULT_ROTATE_ADS } from "../Constants/constant";
import { useRotator } from "../hook/useRotator";

const VIDEO_ACTIVITY_EVENTS = ["mousemove", "keydown", "scroll", "touchmove"];

const HandSwipeIcon = ({ onNext }) => {
  return (
     <button
          onClick={onNext}
          className="
            absolute
            tall-0:bottom-[20rem]
            tall-1:bottom-[50rem]
            tall-2:bottom-[70rem]
            tall-3:bottom-[120rem]
            tall-4:bottom-[180rem]
            bottom-8
            right-8
            bg-black/50
            rounded-full
            p-3
            hover:bg-black/70
            transition
            shadow-md
            flex
            items-center
            justify-center
          "
          aria-label="Next"
        >
          <img src={HandSwipe} alt="hand-swipe" className="w-20 h-20" />
        </button>
  )
}
const ImageAdDisplay = ({ item, mediaItems, currentIndex, onNext, onClose }) => {
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  // Auto-advance to next image after DEFAULT_ROTATE_ADS ms; resets on item change.
  useEffect(() => {
    const timer = setTimeout(() => onNextRef.current(), DEFAULT_ROTATE_ADS);
    return () => clearTimeout(timer);
  }, [item]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onMouseMove={onClose}
      onTouchStart={onClose}
    >
      <div className="absolute inset-0">
        <div
          className="w-full min-h-screen bg-contain bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${item.imageUrl || ShoppingCart})` }}
        />
      </div>

      <div className="relative z-10 w-full h-full flex flex-col justify-end">
        <HandSwipeIcon onNext={onNext} />

        <div className="flex justify-center space-x-2 pb-4">
          {mediaItems.map((_, idx) => (
            <div
              key={idx}
              className={`w-3 h-3 rounded-full ${idx === currentIndex ? "bg-white" : "bg-gray-400"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const VideoAdDisplay = ({ item, loop, onVideoEnd, onClose }) => {
  const containerRef = useRef(null);
  const closeRef = useRef(null);

  // Block activity events from bubbling to window while video plays,
  // so useInactivity in the parent terminal is not reset by video interaction.
  // Clicks on the close button are exempted.
  useEffect(() => {
    if (!containerRef.current) return;
    const stopProp = (e) => {
      if (e.type === "click" && closeRef.current?.contains(e.target)) return;
      e.stopPropagation();
    };
    const el = containerRef.current;
    VIDEO_ACTIVITY_EVENTS.forEach((evt) => el.addEventListener(evt, stopProp));
    return () => VIDEO_ACTIVITY_EVENTS.forEach((evt) => el.removeEventListener(evt, stopProp));
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="absolute inset-0">
        <video
          key={item.videoUrl}
          src={item.videoUrl}
          autoPlay
          muted
          playsInline
          loop={loop}
          onEnded={onVideoEnd}
          className="absolute inset-0 w-full h-full object-contain"
        />
        <HandSwipeIcon />
      </div>

      <div className="relative z-10 w-full h-full">
        <button
          ref={closeRef}
          onClick={onClose}
          className="absolute top-6 right-6 rounded-full bg-black/50 hover:bg-black/70 p-3 transition shadow-md flex items-center justify-center"
          aria-label="Close"
        >
          <X size={28} color="white" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

const DisplayAdvertisement = ({ advertisements, onClose }) => {
  const mediaItems = advertisements ?? [];
  const isSingleVideo = mediaItems.length === 1 && !!mediaItems[0].videoUrl;

  const [currentAdsItem, currentAdsIndex, showNextItem] = useRotator(
    true,
    mediaItems,
    9_999_999,
  );

  if (!mediaItems.length) return null;

  if (currentAdsItem?.videoUrl) {
    return (
      <VideoAdDisplay
        item={currentAdsItem}
        loop={isSingleVideo}
        onVideoEnd={showNextItem}
        onClose={onClose}
      />
    );
  }

  return (
    <ImageAdDisplay
      item={currentAdsItem}
      mediaItems={mediaItems}
      currentIndex={currentAdsIndex}
      onNext={showNextItem}
      onClose={onClose}
    />
  );
};

export default DisplayAdvertisement;
